/**
 * @file        src/pages/insurance/PolicyRequests.jsx
 * @description The admin queue behind the storefront's "Request insurance
 *              policy" button: the customer asks for the insurer's policy
 *              document, this screen is where it is uploaded or refused, and
 *              the customer then downloads it from their own booking page.
 *
 *              ONE component serves both sections; CorporatePolicyRequests.jsx
 *              renders it with `audience="b2b"`. Separate screens, fixed
 *              audience, no picker, not a URL key (CLAUDE.md §9d).
 *
 *              Open requests sort OLDEST FIRST (the repository's ORDER BY) — a
 *              queue worked newest-first leaves the customer who has waited
 *              longest waiting longest.
 *
 *              A rejection REQUIRES a reason because the reason is what reaches
 *              the customer: it is the answer to their request. Refusing with
 *              no reason leaves a request that merely looks closed.
 * @author      Kinetika Labs
 * @created     2026-09-22
 * @changelog
 *   - 2026-09-25 Claude — The customer is now EMAILED on both outcomes (the
 *     document attached, or the refusal with its reason), so the toasts say so.
 *     Nothing went back to them before: the only way to learn a document had
 *     arrived was to revisit the booking page and notice a button had changed.
 *   - 2026-09-25 Claude — Requests against a policy sold WITH A FLIGHT appear
 *     here too, tagged "With flight" and filterable by "Sold via". They were
 *     dropped by the repository's INNER JOIN to `insurance_bookings` — a table
 *     an add-on policy has no row in — so the customer's request reached nobody.
 *   - 2026-09-22 Claude — Initial ("Request insurance policy", admin half).
 */
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReadOnlyListPage from '../flight/ReadOnlyListPage';
import Chip from '../../components/Chip';
import Icon from '../../components/Icon';
import BrandSelect from '../../components/BrandSelect';
import RowMenu from '../../components/RowMenu';
import { get, post, patch, apiErrorMessage } from '../../api/client';
import { toast } from '../../lib/toast';
import Select from '../../components/Select';
import { money, day, dateTime, journeyLabel, customerCell } from './cells';

const CARDS = [
  { key: 'requested', label: 'Awaiting upload', tone: 'card-pend' },
  { key: 'uploaded', label: 'Issued', tone: 'card-ok' },
  { key: 'rejected', label: 'Rejected', tone: 'card-rej' },
];
const URL_KEYS = ['status', 'brand', 'origin'];

// Which checkout sold the policy the request is about. The same two values the
// Insurance List filters on, worded the same way — one product bought two ways,
// fulfilled from this one queue.
const ORIGINS = [
  { value: '', label: 'All requests' },
  { value: 'standalone', label: 'Bought on its own' },
  { value: 'flight', label: 'Sold with a flight' },
];

const statusChip = (s) => (s === 'uploaded'
  ? <Chip tone="ok">Issued</Chip>
  : s === 'rejected' ? <Chip tone="rej">Rejected</Chip> : <Chip tone="pend">Awaiting upload</Chip>);

/** Reject: the reason is required and is shown to the customer. */
function RejectModal({ row, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await patch(`/insurance/policy-requests/${row.id}`, { action: 'reject', reason: reason.trim() });
      toast.ok('Request rejected', 'Your reason has been emailed to the customer.');
      onDone();
    } catch (err) {
      toast.err('Could not reject', apiErrorMessage(err, 'Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 520, width: '96%' }}>
        <div className="lk-modal-head">
          <h3>Reject policy request · {row.booking_reference}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>
        <form className="form-grid" onSubmit={submit}>
          <div className="field full">
            <label>Reason <span className="muted">(the customer sees this)</span></label>
            <textarea
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. The insurer cannot reissue this policy — please contact support."
            />
          </div>
          <div className="form-actions full">
            <button className="btn ghost" type="button" onClick={onClose}>Cancel</button>
            <button className="btn danger" type="submit" disabled={busy || !reason.trim()}>
              {busy ? 'Rejecting…' : 'Reject request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PolicyRequests({
  title = 'Policy Requests',
  subtitle = 'B2C Insurance · policy document requests',
  audience = 'b2c',
} = {}) {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [rejectRow, setRejectRow] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // One hidden input, retargeted at whichever row asked to upload — simpler than
  // one per row and it cannot get out of sync with the table.
  const fileRef = useRef(null);
  const pendingRow = useRef(null);

  const status = params.get('status') || null;
  const brand = params.get('brand') || '';
  const origin = params.get('origin') || '';

  function setFilters(patch) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
    setParams(next, { replace: true });
  }

  const { data: sum } = useQuery({
    queryKey: ['insurance-policy-requests', 'summary', audience],
    queryFn: () => get('/insurance/policy-requests/summary', { params: { audience } }),
  });
  const counts = sum?.data || {};

  const extraParams = { audience };
  for (const k of URL_KEYS) { const v = params.get(k) || ''; if (v) extraParams[k] = v; }

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['insurance-policy-requests'] });
    setRejectRow(null);
  };

  /** Upload the PDF, then attach the URL it returns to the request. */
  async function onFilePicked(e) {
    const file = e.target.files?.[0];
    const row = pendingRow.current;
    e.target.value = '';           // so picking the same file twice still fires
    if (!file || !row) return;

    setBusyId(row.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // A dedicated document endpoint, not the image one: PDFs are rejected
      // there, and this route names the file with 24 random bytes because the
      // /uploads directory is served without authentication.
      const up = await post('/uploads/document', fd);
      await patch(`/insurance/policy-requests/${row.id}`, {
        action: 'upload',
        url: up.data.url,
        // The origin-less path, when this console's upload route returned one.
        // The server prefers it, so a link can be rebuilt from config if the
        // deployment ever moves domain.
        path: up.data.path || undefined,
        filename: up.data.originalName || up.data.filename,
      });
      // The customer is emailed the document by the server — say so, because an
      // admin who thinks nobody was told rings them, and one who thinks somebody
      // was told when nobody was does not.
      toast.ok('Policy sent', `Emailed to ${row.customer_email || 'the customer'}, and ready to download on their booking.`);
      refresh();
    } catch (err) {
      toast.err('Upload failed', apiErrorMessage(err, 'Could not attach the policy document.'));
    } finally {
      setBusyId(null);
      pendingRow.current = null;
    }
  }

  const toolbar = (
    <>
      <div className="stat-row">
        {CARDS.map((c) => (
          <button
            key={c.key}
            className={`stat-card ${c.tone}${status === c.key ? ' active' : ''}`}
            onClick={() => setFilters({ status: status === c.key ? '' : c.key })}
            title={`Filter ${c.label}`}
          >
            <span className="stat-num">{counts[c.key] ?? '—'}</span>
            <span className="stat-label">{c.label}</span>
          </button>
        ))}
        {URL_KEYS.some((k) => params.get(k)) ? (
          <button className="btn danger" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            <Icon name="x" /> Clear Filter
          </button>
        ) : null}
      </div>
      <div className="search-row" style={{ marginTop: 8 }}>
        <div className="field">
          <label>Brand</label>
          <BrandSelect value={brand} onChange={(v) => setFilters({ brand: v })} withAll />
        </div>
        <div className="field">
          <label>Sold via</label>
          <Select value={origin} onChange={(v) => setFilters({ origin: v })} options={ORIGINS} />
        </div>
      </div>
    </>
  );

  const rowActions = (row) => (
    <RowMenu
      label="Actions"
      items={[
        {
          key: 'upload',
          label: row.status === 'uploaded' ? 'Replace document' : 'Upload policy',
          icon: 'plus',
          disabled: busyId === row.id,
          onClick: () => { pendingRow.current = row; fileRef.current?.click(); },
        },
        {
          key: 'download',
          label: 'Open document',
          icon: 'download',
          hidden: row.status !== 'uploaded' || !row.policy_url,
          onClick: () => window.open(row.policy_url, '_blank', 'noopener'),
        },
        {
          key: 'reject',
          label: 'Reject',
          icon: 'x',
          danger: true,
          hidden: row.status === 'rejected',
          onClick: () => setRejectRow(row),
        },
      ]}
    />
  );

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={onFilePicked}
      />

      <ReadOnlyListPage
        title={title}
        subtitle={subtitle}
        endpoint="/insurance/policy-requests"
        queryRoot="insurance-policy-requests"
        searchLabel="Booking Ref / Policy No."
        actionsLabel="Action"
        rowActions={rowActions}
        toolbar={toolbar}
        extraParams={extraParams}
        showIndex={false}
        dense
        columns={[
          {
            key: 'booking_reference',
            label: 'Booking',
            render: (v, r) => (
              <div>
                <div className="mono"><b>{v || '—'}</b></div>
                <div className="muted" style={{ fontSize: '.8em' }}>
                  {journeyLabel(r.journey)} · {r.insurer || '—'}
                </div>
                {/* Which checkout sold it. On an add-on the reference above is
                    the FLIGHT's, so saying so is what stops it reading as a
                    policy reference that cannot be found on the Insurance
                    List — and it is where the customer's ticket lives. */}
                {r.origin === 'flight' ? (
                  <Chip tone="pend">With flight{r.pnr_code ? ` · ${r.pnr_code}` : ''}</Chip>
                ) : null}
              </div>
            ),
          },
          {
            key: 'policy_id',
            label: 'Policy No.',
            render: (v) => (v ? <span className="mono">{v}</span> : <span className="muted">Not issued</span>),
          },
          {
            key: 'start_date',
            label: 'Cover',
            render: (v, r) => (
              <div>
                <div>{day(v)}</div>
                <div className="muted" style={{ fontSize: '.8em' }}>to {day(r.end_date)}</div>
              </div>
            ),
          },
          { key: 'customer_name', label: 'Customer', render: customerCell },
          {
            key: 'requested_at',
            label: 'Requested',
            render: (v, r) => (
              <div>
                <div>{dateTime(v)}</div>
                {/* A chase is the signal for whoever works this queue. */}
                {Number(r.request_count) > 1
                  ? <div className="muted" style={{ fontSize: '.8em' }}>asked {r.request_count}×</div>
                  : null}
              </div>
            ),
          },
          {
            key: 'status',
            label: 'Status',
            render: (v, r) => (
              <div>
                {statusChip(v)}
                {v === 'uploaded' && r.uploaded_at
                  ? <div className="muted" style={{ fontSize: '.8em' }}>{dateTime(r.uploaded_at)}</div>
                  : null}
                {v === 'rejected' && r.admin_note
                  ? <div className="muted" style={{ fontSize: '.8em' }} title={r.admin_note}>{r.admin_note.slice(0, 40)}</div>
                  : null}
              </div>
            ),
          },
          { key: 'total_paid', label: 'Amount', align: 'right', render: (v) => money(v) },
        ]}
      />

      {rejectRow ? (
        <RejectModal row={rejectRow} onClose={() => setRejectRow(null)} onDone={refresh} />
      ) : null}
    </>
  );
}
